/**
 * WP-V2-WC — bounds świata + follow-kamera z deadzone.
 *
 * setupWorld(scene, { worldW, worldH, bg })
 * follow(target, { lerp: 0.12, deadzone: 120×80 })
 */
import type Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '@pieskowo/shared';
import { stepFollowCamera, type FollowCameraState } from './cameraLogic.js';
import {
  DEFAULT_DEADZONE,
  DEFAULT_FOLLOW_LERP,
  type DeadzoneSize,
} from './types.js';

export type SetupWorldOpts = {
  worldW: number;
  worldH: number;
  /**
   * Tło: klucz tekstury Phaser, kolor numeryczny, albo pominięte
   * (scena sama rysuje tło).
   */
  bg?: string | number;
};

export type FollowOpts = {
  /** Domyślnie 0.12 — płynne doganianie bez szarpania. */
  lerp?: number;
  /** Domyślnie 120×80. */
  deadzone?: DeadzoneSize;
};

export type WorldCameraHandle = {
  worldW: number;
  worldH: number;
  destroy(): void;
};

type SceneFollowBag = {
  state: FollowCameraState;
  target: { x: number; y: number };
  opts: FollowOpts;
  worldW: number;
  worldH: number;
};

const FOLLOW_KEY = '__worldFollow' as const;
const FOLLOW_BOUND = '__worldFollowBound' as const;

/**
 * Ustawia bounds kamery (i opcjonalnie tło na cały świat).
 * Viewport gry pozostaje GAME_WIDTH×GAME_HEIGHT.
 */
export function setupWorld(
  scene: Phaser.Scene,
  opts: SetupWorldOpts,
): WorldCameraHandle {
  const { worldW, worldH, bg } = opts;
  const cam = scene.cameras.main;
  cam.setBounds(0, 0, worldW, worldH);

  if (typeof bg === 'number') {
    cam.setBackgroundColor(bg);
  } else if (typeof bg === 'string' && scene.textures.exists(bg)) {
    scene.add
      .image(worldW / 2, worldH / 2, bg)
      .setDisplaySize(worldW, worldH)
      .setDepth(-1000)
      .setScrollFactor(1);
  }

  return {
    worldW,
    worldH,
    destroy() {
      cam.stopFollow();
      const bag = scene as Phaser.Scene & { [FOLLOW_KEY]?: SceneFollowBag };
      delete bag[FOLLOW_KEY];
    },
  };
}

/**
 * Kamera podąża za celem z lerp + deadzone.
 * GameObject Phaser → startFollow + setDeadzone.
 * Zwykły {x,y} → ręczny follow w event 'update' sceny.
 */
export function follow(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject | { x: number; y: number },
  opts: FollowOpts = {},
): void {
  const lerp = opts.lerp ?? DEFAULT_FOLLOW_LERP;
  const dz = opts.deadzone ?? DEFAULT_DEADZONE;
  const cam = scene.cameras.main;

  const isGo =
    target != null &&
    typeof (target as Phaser.GameObjects.GameObject).once === 'function';

  if (isGo) {
    cam.startFollow(target as Phaser.GameObjects.GameObject, true, lerp, lerp);
    cam.setDeadzone(dz.width, dz.height);
    return;
  }

  const plain = target as { x: number; y: number };
  const bounds = cam.getBounds();
  const worldW = bounds.width > 0 ? bounds.width : GAME_WIDTH;
  const worldH = bounds.height > 0 ? bounds.height : GAME_HEIGHT;

  const bag = scene as Phaser.Scene & {
    [FOLLOW_KEY]?: SceneFollowBag;
    [FOLLOW_BOUND]?: boolean;
  };
  bag[FOLLOW_KEY] = {
    state: { scrollX: cam.scrollX, scrollY: cam.scrollY },
    target: plain,
    opts,
    worldW,
    worldH,
  };

  if (!bag[FOLLOW_BOUND]) {
    bag[FOLLOW_BOUND] = true;
    scene.events.on('update', () => {
      const f = bag[FOLLOW_KEY];
      if (!f) return;
      f.state = stepFollowCamera(f.state, f.target.x, f.target.y, {
        lerp: f.opts.lerp ?? DEFAULT_FOLLOW_LERP,
        deadzone: f.opts.deadzone ?? DEFAULT_DEADZONE,
        viewW: GAME_WIDTH,
        viewH: GAME_HEIGHT,
        worldW: f.worldW,
        worldH: f.worldH,
      });
      cam.setScroll(f.state.scrollX, f.state.scrollY);
    });
  }
}

/** Wygodny obiekt-wrapper (styl OOP dla scen). */
export class WorldCamera {
  private handle: WorldCameraHandle | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  setupWorld(opts: SetupWorldOpts): this {
    this.handle?.destroy();
    this.handle = setupWorld(this.scene, opts);
    return this;
  }

  follow(
    target: Phaser.GameObjects.GameObject | { x: number; y: number },
    opts: FollowOpts = {},
  ): this {
    follow(this.scene, target, opts);
    return this;
  }

  get worldW(): number {
    return this.handle?.worldW ?? GAME_WIDTH;
  }

  get worldH(): number {
    return this.handle?.worldH ?? GAME_HEIGHT;
  }

  destroy(): void {
    this.handle?.destroy();
    this.handle = null;
  }
}
